import React, { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import JoinNowBlock from '../../../../components/JoinNowBlock';
import {
  GET_CONTINUE_WATCHING_VIDEO_LINKS,
  GET_VIDEOS_BY_EXPERT,
} from '../../../../graphql/videos';
import { Query, QueryListContinuingWatchingLinksArgs, Video } from '../../../../types';
import { generateContinueWatchingLink, getLinksObj } from '../../../../utils/helpers';
import Expert from '../Expert';
import LogoutSection from '../LogoutSection/LogoutSection';
import Overview from '../Overview/Overview';
import Summary from '../Summary';
import VideoTabs from '../VideoTabs';
import { useJoinNowStyles } from './styles';

export enum TabsEnum {
  Overview = 'overview',
  Summary = 'summary',
  Expert = 'expert',
  Reviews = 'reviews',
}

const VideoTabsContainer = ({ video, userId, isXApiPage, rating, showTabs }) => {
  const { t } = useTranslation();
  const joinNowClasses = useJoinNowStyles();
  const { data, loading } = useQuery(GET_VIDEOS_BY_EXPERT, {
    variables: {
      id: video?.user?.id,
    },
  });

  const listVideos = data?.listVideos;
  const videos = listVideos ? listVideos.filter((item) => item.id && item.id !== video.id) : [];
  const continueWatchingIds = videos ? videos.map((item) => item?.id).filter((item) => !!item) : [];

  const { data: linksData, loading: loadingLinks } = useQuery<
    Pick<Query, 'listContinuingWatchingLinks'>,
    QueryListContinuingWatchingLinksArgs
  >(GET_CONTINUE_WATCHING_VIDEO_LINKS, {
    variables: {
      filter: {
        user_id: userId ? userId : '',
        video_ids: continueWatchingIds as string[],
      },
    },
    skip: !userId || loading || continueWatchingIds.length === 0,
  });

  const lastPositionData = getLinksObj(linksData?.listContinuingWatchingLinks);

  const videoWithUrl = videos.map((item) => ({
    ...item,
    url: generateContinueWatchingLink(
      `/videos/${item?.video_url}/`,
      lastPositionData[item?.id]?.chapter,
      lastPositionData[item?.id]?.time,
    ),
  }));

  const tabs = useMemo(() => getTabs(video), [video]);

  const overviewExists = useMemo(() => tabs[TabsEnum.Overview] || tabs[TabsEnum.Overview] === 0, [
    tabs,
  ]);
  const summaryExists = useMemo(() => tabs[TabsEnum.Summary] || tabs[TabsEnum.Summary] === 0, [
    tabs,
  ]);
  return (
    <div>
      {!showTabs ? (
        <>
          {overviewExists && (
            <LogoutSection title={t('common.overview')}>
              <Overview
                keyLearningObjectives={video.key_learning_objectives}
                summary={video.executive_summary}
              />
            </LogoutSection>
          )}
          <JoinNowBlock
            isLoggedIn={false}
            classes={joinNowClasses}
            description={t('page.video.logoutFreeTrial')}
            btnCaption={t('button.startTrial')}
            data-testid="joinNowBlock"
          />
          {summaryExists && (
            <LogoutSection title={t('common.summary')}>
              <Summary summary={video.summary} />
            </LogoutSection>
          )}
          <LogoutSection title={t('common.expert')}>
            <Expert
              expert={video.user}
              videos={videoWithUrl}
              loadingVideos={loading}
              isXApiPage={isXApiPage}
            />
          </LogoutSection>
        </>
      ) : (
        <VideoTabs
          video={video}
          isXApiPage={isXApiPage}
          rating={rating}
          videoWithUrl={videoWithUrl}
          loading={loading || loadingLinks}
          tabs={tabs}
          overviewExists={overviewExists}
          summaryExists={summaryExists}
        />
      )}
    </div>
  );
};

export default VideoTabsContainer;

const checkSummaryExists = (summary) => {
  if (summary) {
    const parsed = JSON.parse(summary);
    return parsed && Array.isArray(parsed) && parsed.length > 0;
  } else {
    return false;
  }
};

const getTabs = (video: Video) => {
  const arr = [
    {
      tab: TabsEnum.Overview,
      condition: Boolean(video.key_learning_objectives || video.executive_summary),
    },
    {
      tab: TabsEnum.Summary,
      condition: checkSummaryExists(video.summary),
    },
    {
      tab: TabsEnum.Expert,
      condition: true,
    },
    {
      tab: TabsEnum.Reviews,
      condition: true,
    },
  ];

  const res = {};
  let index = 0;

  arr.forEach((item) => {
    if (item.condition) {
      res[item.tab] = index;
      index++;
    }
  });

  return res;
};
